// src/utils/mediaConversion/mapping.ts

import type {
  MediaConversionJobDTO,
  MediaJobStatus,
} from "@/types/mediaConversion/mediaJob";
import type {
  ConversionStage,
  MediaConversionPanelState,
} from "@/types/mediaConversion/mediaConversionTypes";
import { isHeartbeatStale } from "./timing";

/* -------------------------------------------
 * Helpers
 * ----------------------------------------- */

function clamp(n: number, a = 0, b = 100) {
  return Math.max(a, Math.min(b, n));
}
function clamp01(n: number) {
  return Math.max(0, Math.min(1, n));
}

function pickLastIso(
  ...candidates: Array<string | null | undefined>
): string | null {
  const valid = candidates.filter(Boolean) as string[];
  if (!valid.length) return null;
  return valid.sort((a, b) => Date.parse(b) - Date.parse(a))[0] ?? null;
}

/**
 * ⭐ CRITICAL:
 * Always pick the SAME logical pipeline job.
 * This eliminates random 50/80 jumps.
 */
function pickDriverJob(
  jobs: MediaConversionJobDTO[]
): MediaConversionJobDTO | null {
  if (!jobs.length) return null;

  // 1) Prefer active processing job
  const processing = jobs.find((j) => j.status === "processing");
  if (processing) return processing;

  // 2) Then queued
  const queued = jobs.find((j) => j.status === "queued");
  if (queued) return queued;

  // 3) Fallback: most recently updated
  const sorted = [...jobs].sort((a, b) => {
    const ta =
      Date.parse(a.updated_at || a.created_at || "") || 0;
    const tb =
      Date.parse(b.updated_at || b.created_at || "") || 0;
    return tb - ta;
  });

  return sorted[0] ?? jobs[0];
}

function prettyLabelForKey(key: string): string {
  const k = (key || "").toLowerCase();

  if (k === "prepare" || k === "preparing") return "Preparing";
  if (k === "queued") return "Queued";
  if (k === "processing") return "Processing";
  if (k === "source") return "Encoding original quality";
  if (k === "finalize" || k === "finalizing") return "Publishing";
  if (k === "ready") return "Ready";

  if (/\b1080p\b/.test(k)) return "Encoding 1080p";
  if (/\b720p\b/.test(k)) return "Encoding 720p";
  if (/\b480p\b/.test(k)) return "Encoding 480p";

  return key;
}

function hintForKey(key: string): string | undefined {
  const k = (key || "").toLowerCase();

  if (k === "prepare" || k === "preparing")
    return "Preparing your video for encoding";

  if (k === "queued") return "Waiting for a worker…";
  if (k === "processing") return "Preparing pipeline…";

  if (k === "source")
    return "Keeping the original viewing quality";

  if (k === "finalize" || k === "finalizing")
    return "Publishing files safely…";

  if (/\b1080p\b/.test(k)) return "Best quality";
  if (/\b720p\b/.test(k)) return "Balanced quality";
  if (/\b480p\b/.test(k)) return "Mobile friendly";

  return undefined;
}

function sumPlanWeight(job: MediaConversionJobDTO): number | null {
  const plan = job.stage_plan;
  if (!plan || !plan.length) return null;
  const s = plan.reduce(
    (acc, p) => acc + (Number(p.weight) || 0),
    0
  );
  return s > 0 ? s : null;
}

function inferCompletedWeight(job: MediaConversionJobDTO): number {
  const plan = job.stage_plan;
  if (!plan || !plan.length) return 0;

  const currentKey = (job.stage || "").toString();
  const idx = plan.findIndex((p) => p.key === currentKey);
  if (idx <= 0) return 0;

  let sum = 0;
  for (let i = 0; i < idx; i++) {
    sum += Number(plan[i]?.weight) || 0;
  }
  return sum;
}

function currentStageWeight(job: MediaConversionJobDTO): number {
  if (
    typeof job.stage_weight === "number" &&
    job.stage_weight >= 0
  ) {
    return job.stage_weight;
  }
  const plan = job.stage_plan;
  if (!plan || !plan.length) return 0;
  const key = (job.stage || "").toString();
  const found = plan.find((p) => p.key === key);
  return Number(found?.weight) || 0;
}

/* -------------------------------------------
 * Filters
 * ----------------------------------------- */

export function filterJobsByField(
  jobs: MediaConversionJobDTO[],
  fieldName?: string
) {
  const f = (fieldName || "").trim();
  if (!f) return jobs;
  return jobs.filter((j) => j.field_name === f);
}

/* -------------------------------------------
 * Overall status
 * ----------------------------------------- */

export function computeOverallStatus(
  jobs: MediaConversionJobDTO[]
): MediaJobStatus | "idle" {
  if (!jobs.length) return "idle";

  if (jobs.some((j) => j.status === "failed")) return "failed";
  if (jobs.some((j) => j.status === "processing"))
    return "processing";
  if (jobs.some((j) => j.status === "queued")) return "queued";
  if (jobs.every((j) => j.status === "canceled"))
    return "canceled";
  if (jobs.every((j) => j.status === "done")) return "done";

  return "processing";
}



/* -------------------------------------------
 * Weighted percent (MONOTONIC & TRUE)
 * ----------------------------------------- */

export function computeWeightedPercent(
  jobs: MediaConversionJobDTO[]
): number {
  const job = pickDriverJob(jobs);
  if (!job) return 0;

  // Terminal success must always display complete.
  if (job.status === "done") return 100;

  // Canceled/failed should not jump to 100.
  if (job.status === "failed" || job.status === "canceled") {
    return clamp(Number(job.progress || 0));
  }

  const total =
    (typeof job.stage_total_weight === "number" && job.stage_total_weight > 0
      ? job.stage_total_weight
      : sumPlanWeight(job)) ?? null;

  // Fallback for audio/image or older jobs without weighted metadata.
  if (!total || total <= 0) {
    return clamp(Number(job.progress || 0));
  }

  const frac =
    job.stage_progress == null
      ? 0
      : clamp01(Number(job.stage_progress));

  const weight = currentStageWeight(job);

  const completed =
    typeof job.stage_completed_weight === "number"
      ? Math.max(0, job.stage_completed_weight)
      : inferCompletedWeight(job);

  const weighted = (completed + weight * frac) / total;

  return clamp(Math.round(weighted * 100));
}


/* -------------------------------------------
 * Headline & Detail
 * ----------------------------------------- */

export function computeHeadline(
  jobs: MediaConversionJobDTO[],
  overall: MediaJobStatus | "idle"
): { headline: string; detail?: string } {
  switch (overall) {
    case "idle":
      return {
        headline: "Preparing your media…",
        detail: "This will only take a moment.",
      };

    case "queued":
      return {
        headline: "Your upload is in line",
        detail:
          "You’re free to continue — we’ll handle this in the background.",
      };

    case "processing": {
      const msg = jobs.map((j) => j.message).filter(Boolean).pop();
      return {
        headline: msg
          ? String(msg)
          : "Processing your media",
        detail:
          "You can safely leave this page. Your media will be ready shortly.",
      };
    }

    case "done":
      return {
        headline: "Your media is ready",
        detail: "Everything is set and available to view.",
      };

    case "canceled":
      return {
        headline: "Conversion stopped",
        detail: "This item was removed or replaced.",
      };

    case "failed": {
      const err = jobs.map((j) => j.error).filter(Boolean).pop();
      return {
        headline: "Something went wrong",
        detail: err
          ? String(err).slice(0, 160)
          : "Please try again in a moment.",
      };
    }

    default:
      return { headline: "Working on your media…" };
  }
}

/* -------------------------------------------
 * Stages (driver-based)
 * ----------------------------------------- */

export function computeStages(
  jobs: MediaConversionJobDTO[],
  overall: MediaJobStatus | "idle"
): ConversionStage[] {
  const job = pickDriverJob(jobs);
  if (!job) return [];

  const plan = job.stage_plan ?? [];

  if (plan.length) {
    const completed =
      typeof job.stage_completed_weight === "number"
        ? Math.max(0, job.stage_completed_weight)
        : inferCompletedWeight(job);

    const currentKey = (job.stage || "").toString();

    const doneSet = new Set<string>();
    let sum = 0;
    for (const p of plan) {
      const w = Number(p.weight) || 0;
      if (sum + w <= completed + 1e-9) {
        doneSet.add(p.key);
        sum += w;
      } else {
        break;
      }
    }

    const stages: ConversionStage[] = plan.map((p) => {
      let status: ConversionStage["status"] = "todo";

      if (overall === "done") status = "done";
      else if (overall === "failed" || overall === "canceled") {
        if (doneSet.has(p.key)) status = "done";
        else if (p.key === currentKey) status = "error";
      } else {
        if (doneSet.has(p.key)) status = "done";
        else if (
          overall === "processing" &&
          p.key === currentKey
        )
          status = "active";
        else if (
          overall === "queued" &&
          p.key === plan[0]?.key
        )
          status = "active";
      }

      return {
        key: p.key,
        label: p.label || prettyLabelForKey(p.key),
        status,
        weight: Number(p.weight) || 0,
        hint: hintForKey(p.key),
      };
    });

    stages.push({
      key: "ready",
      label: "Ready",
      status: overall === "done" ? "done" : "todo",
      weight: 0,
    });

    return stages;
  }

  // minimal fallback
  const base: ConversionStage[] = [
    { key: "queued", label: "Queued", status: "todo", weight: 1 },
    {
      key: "processing",
      label: "Processing",
      status: "todo",
      weight: 1,
    },
    {
      key: "finalize",
      label: "Finalizing",
      status: "todo",
      weight: 1,
    },
    { key: "ready", label: "Ready", status: "todo", weight: 1 },
  ];

  if (overall === "idle" || overall === "queued")
    base[0].status = "active";
  else if (overall === "processing") {
    base[0].status = "done";
    base[1].status = "active";
  } else if (overall === "done")
    base.forEach((s) => (s.status = "done"));
  else if (overall === "failed" || overall === "canceled") {
    base[0].status = "done";
    base[1].status = "error";
  }

  return base;
}

/* -------------------------------------------
 * Panel State
 * ----------------------------------------- */

export function buildPanelState(
  jobs: MediaConversionJobDTO[],
  opts?: { staleAfterMs?: number }
): MediaConversionPanelState {
  const staleAfterMs = opts?.staleAfterMs ?? 45_000;

  const overall = computeOverallStatus(jobs);
  const percent = computeWeightedPercent(jobs);
  const { headline, detail } = computeHeadline(jobs, overall);
  const stages = computeStages(jobs, overall);

  const lastHeartbeatAt = pickLastIso(
    ...jobs.map((j) => j.heartbeat_at ?? null)
  );
  const lastUpdatedAt = pickLastIso(
    ...jobs.map((j) => j.updated_at ?? null)
  );

  const isStalled =
    overall === "processing" &&
    isHeartbeatStale(
      lastHeartbeatAt,
      lastUpdatedAt,
      Date.now(),
      staleAfterMs
    );

  return {
    overallStatus: overall,
    percent,
    headline,
    detail,
    stages,
    isStalled,
    stalledHint: isStalled
      ? "Still working in the background — thanks for your patience."
      : undefined,
    lastHeartbeatAt,
    lastUpdatedAt,
  };
}
